import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line,
} from 'recharts';
import { BarChart3, TrendingUp, Target, Award } from 'lucide-react';
import { useAnalyticsSummary, useDailyPnl, useSymbolBreakdown, useStrategyBreakdown } from '@/hooks/useApi';
import { StatCard, Card, LoadingSpinner } from '@/components/UI';
import { formatCurrency, formatPercent, pnlColor } from '@/utils';

const CHART_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#818cf8', '#34d399', '#fbbf24', '#f87171'];

export function AnalyticsPage() {
  const { data: summary, isLoading: sumLoading } = useAnalyticsSummary();
  const { data: dailyPnl, isLoading: pnlLoading } = useDailyPnl(undefined, 30);
  const { data: symbolData, isLoading: symbolLoading } = useSymbolBreakdown();
  const { data: stratData, isLoading: stratLoading } = useStrategyBreakdown();

  if (sumLoading) return <LoadingSpinner text="Loading analytics..." />;

  const pnlChartData = (dailyPnl ?? []).map((d: { date: string; pnl: number }) => ({
    date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    pnl: d.pnl,
  }));

  const symbolChartData = (symbolData ?? []).map((p: { symbol: string; pnl: number; win_rate: number }) => ({
    name: p.symbol,
    pnl: p.pnl,
    winRate: p.win_rate * 100,
  }));

  const stratChartData = (stratData ?? []).map((s: { strategy: string; pnl: number; win_rate: number }) => ({
    name: s.strategy.replace(/_/g, ' '),
    pnl: s.pnl,
    winRate: s.win_rate * 100,
  }));

  return (
    <div className="space-y-6 animate-in">
      <h1 className="text-2xl font-bold text-surface-100">Analytics</h1>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total P&L"
          value={formatCurrency(summary?.total_pnl ?? 0)}
          icon={<TrendingUp className="w-5 h-5" />}
        />
        <StatCard
          label="Win Rate"
          value={formatPercent((summary?.win_rate ?? 0) * 100)}
          icon={<Target className="w-5 h-5" />}
        />
        <StatCard
          label="Profit Factor"
          value={(summary?.profit_factor ?? 0).toFixed(2)}
          icon={<Award className="w-5 h-5" />}
        />
        <StatCard
          label="Max Drawdown"
          value={formatPercent(summary?.max_drawdown ?? 0)}
        />
      </div>

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Performance Metrics">
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: 'Total Trades', value: summary?.total_trades ?? 0 },
              { label: 'Winning', value: summary?.winning_trades ?? 0 },
              { label: 'Losing', value: summary?.losing_trades ?? 0 },
              { label: 'Avg Profit', value: formatCurrency(summary?.avg_profit ?? 0) },
              { label: 'Avg Loss', value: formatCurrency(summary?.avg_loss ?? 0) },
              { label: 'Best Trade', value: formatCurrency(summary?.best_trade ?? 0) },
              { label: 'Worst Trade', value: formatCurrency(summary?.worst_trade ?? 0) },
              { label: 'Sharpe Ratio', value: (summary?.sharpe_ratio ?? 0).toFixed(2) },
            ].map(({ label, value }) => (
              <div key={label}>
                <p className="text-xs text-surface-500">{label}</p>
                <p className="text-sm font-medium text-surface-200">{value}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Daily PnL Chart */}
        <Card title="Daily P&L" subtitle="Last 30 days">
          <div className="h-56">
            {pnlLoading ? (
              <LoadingSpinner />
            ) : pnlChartData.length === 0 ? (
              <div className="flex items-center justify-center h-full text-sm text-surface-500">No data</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pnlChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 10 }} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 10 }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '8px', color: '#e2e8f0' }}
                  />
                  <Bar dataKey="pnl" radius={[4, 4, 0, 0]}>
                    {pnlChartData.map((entry: { pnl: number }, idx: number) => (
                      <Cell key={idx} fill={entry.pnl >= 0 ? '#10b981' : '#ef4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>

      {/* Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Symbol Breakdown */}
        <Card title="Symbol Breakdown">
          <div className="h-56">
            {symbolLoading ? (
              <LoadingSpinner />
            ) : symbolChartData.length === 0 ? (
              <div className="flex items-center justify-center h-full text-sm text-surface-500">No data</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={symbolChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis type="number" tick={{ fill: '#64748b', fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} width={80} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '8px', color: '#e2e8f0' }}
                  />
                  <Bar dataKey="pnl" radius={[0, 4, 4, 0]}>
                    {symbolChartData.map((_: unknown, idx: number) => (
                      <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        {/* Strategy Breakdown */}
        <Card title="Strategy Breakdown">
          <div className="h-56">
            {stratLoading ? (
              <LoadingSpinner />
            ) : stratChartData.length === 0 ? (
              <div className="flex items-center justify-center h-full text-sm text-surface-500">No data</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stratChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis type="number" tick={{ fill: '#64748b', fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} width={120} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '8px', color: '#e2e8f0' }}
                  />
                  <Bar dataKey="pnl" radius={[0, 4, 4, 0]}>
                    {stratChartData.map((_: unknown, idx: number) => (
                      <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
